import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { EmailEditor } from '~/components/features/communications/email-editor';
import { EmailPreview } from '~/components/features/communications/email-preview';
import { SmsEditor } from '~/components/features/communications/sms-editor';
import { SmsPreview } from '~/components/features/communications/sms-preview';
import { Button } from '~/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { api } from '~/lib/api';
import { communicationsQueryOptions } from '~/lib/communications';

type ActiveTab = 'sms' | 'email';

export type MessageType = 'invite' | 'reminder';

export interface MessageTemplateEditorProps {
  orgId: string;
  deptId: string;
  messageType: MessageType;
  lang: string;
}

export function MessageTemplateEditor({
  orgId,
  deptId,
  messageType,
  lang
}: MessageTemplateEditorProps): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(communicationsQueryOptions(orgId, deptId, lang));
  const emailData = data?.[messageType].email;
  const smsData = data?.[messageType].sms;

  const [activeTab, setActiveTab] = useState<ActiveTab>('sms');
  const [emailSubject, setEmailSubject] = useState<string>(emailData?.subject ?? '');
  const [emailBody, setEmailBody] = useState<string>(emailData?.body ?? '');
  const [smsBody, setSmsBody] = useState<string>(smsData?.body ?? '');

  useEffect(() => {
    if (emailData) {
      setEmailSubject(emailData.subject);
      setEmailBody(emailData.body);
    }
    if (smsData) setSmsBody(smsData.body);
  }, [emailData, smsData]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ['communications', 'templates', orgId, deptId]
    });
  };

  const saveEmailMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].department[':deptId'].communications[
        'email-templates'
      ].$put({
        param: { orgId, deptId },
        json: {
          departmentId: deptId,
          type: messageType,
          language: lang,
          subject: emailSubject,
          body: emailBody
        }
      });
      if (!res.ok) throw new Error(t('errors.unexpected'));
    },
    onSuccess: () => {
      invalidate();
      toast.success(t('communications.actions.saved'));
    },
    onError: () => toast.error(t('errors.unexpected'))
  });

  const saveSmsMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].department[':deptId'].communications[
        'sms-templates'
      ].$put({
        param: { orgId, deptId },
        json: { departmentId: deptId, type: messageType, language: lang, body: smsBody }
      });
      if (!res.ok) throw new Error(t('errors.unexpected'));
    },
    onSuccess: () => {
      invalidate();
      toast.success(t('communications.actions.saved'));
    },
    onError: () => toast.error(t('errors.unexpected'))
  });

  const resetEmailMutation = useMutation({
    mutationFn: async () => {
      const id = emailData?.id;
      if (!id) return;
      const res = await api.api.org[':orgId'].department[':deptId'].communications[
        'email-templates'
      ][':templateId'].$delete({ param: { orgId, deptId, templateId: id } });
      if (!res.ok) throw new Error(t('errors.unexpected'));
    },
    onSuccess: () => {
      invalidate();
      toast.success(t('communications.actions.resetDone'));
    },
    onError: () => toast.error(t('errors.unexpected'))
  });

  const resetSmsMutation = useMutation({
    mutationFn: async () => {
      const id = smsData?.id;
      if (!id) return;
      const res = await api.api.org[':orgId'].department[':deptId'].communications['sms-templates'][
        ':templateId'
      ].$delete({ param: { orgId, deptId, templateId: id } });
      if (!res.ok) throw new Error(t('errors.unexpected'));
    },
    onSuccess: () => {
      invalidate();
      toast.success(t('communications.actions.resetDone'));
    },
    onError: () => toast.error(t('errors.unexpected'))
  });

  const tab = {
    sms: {
      isSaving: saveSmsMutation.isPending,
      isResetting: resetSmsMutation.isPending,
      isCustomized: smsData?.isCustomized ?? false,
      isUnchanged: smsBody === smsData?.body
    },
    email: {
      isSaving: saveEmailMutation.isPending,
      isResetting: resetEmailMutation.isPending,
      isCustomized: emailData?.isCustomized ?? false,
      isUnchanged: emailSubject === emailData?.subject && emailBody === emailData.body
    }
  } as const;

  const { isSaving, isResetting, isCustomized, isUnchanged } = tab[activeTab];

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => {
        setActiveTab(v as ActiveTab);
      }}
    >
      <TabsList variant="line">
        <TabsTrigger value="sms" className="data-active:text-primary data-active:after:bg-primary">
          {t('communications.tabs.sms')}
        </TabsTrigger>
        <TabsTrigger
          value="email"
          className="data-active:text-primary data-active:after:bg-primary"
        >
          {t('communications.tabs.email')}
        </TabsTrigger>
      </TabsList>

      <div className="grid grid-cols-1 gap-8 pt-2 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          {isLoading ? (
            <div className="h-48 animate-pulse rounded-lg bg-muted" />
          ) : (
            <>
              <TabsContent value="sms" className="flex-none">
                <SmsEditor
                  value={smsBody}
                  onChange={setSmsBody}
                  disabled={isSaving || isResetting}
                />
              </TabsContent>
              <TabsContent value="email" className="flex-none">
                <EmailEditor
                  subject={emailSubject}
                  body={emailBody}
                  onSubjectChange={setEmailSubject}
                  onBodyChange={setEmailBody}
                  disabled={isSaving || isResetting}
                />
              </TabsContent>
            </>
          )}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                if (activeTab === 'sms') {
                  if (!isCustomized) setSmsBody(smsData?.body ?? '');
                  resetSmsMutation.mutate();
                } else {
                  if (!isCustomized) {
                    setEmailBody(emailData?.body ?? '');
                    setEmailSubject(emailData?.subject ?? '');
                  }
                  resetEmailMutation.mutate();
                }
              }}
              disabled={isResetting || isSaving || !isCustomized}
            >
              {t('communications.actions.reset')}
            </Button>
            <Button
              onClick={() => {
                if (activeTab === 'sms') saveSmsMutation.mutate();
                else saveEmailMutation.mutate();
              }}
              disabled={isSaving || isUnchanged}
            >
              {t('communications.actions.save')}
            </Button>
          </div>
        </div>
        <div className="hidden lg:flex lg:flex-col lg:items-center lg:pt-2">
          {activeTab === 'sms' ? (
            <SmsPreview body={smsBody} />
          ) : (
            <EmailPreview subject={emailSubject} body={emailBody} />
          )}
        </div>
      </div>
    </Tabs>
  );
}
